import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import WalletOutlinedIcon from '@mui/icons-material/WalletOutlined';
import StarIcon from '@mui/icons-material/Star';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { can } from '../../utils/permissions';

const inputSx = (colors) => ({
  backgroundColor: colors.primary[400],
  color: colors.grey[100],
  borderRadius: 1,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[500] },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[500] },
  '& .MuiSvgIcon-root': { color: colors.grey[200] },
});

const labelSx = (colors) => ({
  color: colors.grey[200],
  '&.Mui-focused': { color: colors.greenAccent[400] },
});

function ReportToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5, gap: 1 }}>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const StatCard = ({ icon: Icon, label, value, sub, colors }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
      border: `1px solid ${colors.grey[700]}`,
      borderLeft: `4px solid ${colors.greenAccent[500]}`,
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={1.5}>
      <Box
        sx={{
          p: 1,
          borderRadius: 1.5,
          bgcolor: `${colors.blueAccent[700]}88`,
          color: colors.greenAccent[400],
          display: 'flex',
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="caption" sx={{ color: colors.grey[300], textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold" sx={{ color: colors.grey[100], lineHeight: 1.2, mt: 0.5 }}>
          {value}
        </Typography>
        {sub ? (
          <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.75 }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
    </Box>
  </Paper>
);

const GuidePerformanceReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [searchParams] = useSearchParams();

  const [guides, setGuides] = useState([]);
  const [guideId, setGuideId] = useState('');

  useEffect(() => {
    const g = searchParams.get('guideId');
    if (g) setGuideId(String(g));
  }, [searchParams]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [pageSize, setPageSize] = useState(25);

  const selectedGuideLabel = useMemo(() => {
    const g = guides.find((x) => String(x.id) === String(guideId));
    if (!g) return '';
    return [g.name, g.surname].filter(Boolean).join(' ');
  }, [guides, guideId]);

  useEffect(() => {
    const loadGuides = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_APILINK}/guides?page=1&limit=500&sortBy=u.id&sortOrder=ASC&filter={}`,
          { headers: { token } }
        );
        const data = await res.json();
        if (data?.success && Array.isArray(data.guides)) {
          setGuides(data.guides);
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadGuides();
  }, [token]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (guideId) params.set('guideId', guideId);
    return params.toString();
  }, [guideId]);

  const fetchReport = useCallback(async () => {
    if (!guideId) {
      return;
    }
    setError('');
    setLoading(true);
    try {
      const qs = buildQuery();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/guide-performance?${qs}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (!data.success) {
        setRows([]);
        setTotals(null);
        setError(data.message || 'Failed to load report');
        return;
      }
      setRows(data.data?.rows || []);
      setTotals(data.data?.totals || null);
    } catch (e) {
      setRows([]);
      setTotals(null);
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [guideId, token, buildQuery]);

  useEffect(() => {
    if (!guideId) {
      setRows([]);
      setTotals(null);
      setError('');
      return;
    }
    fetchReport();
  }, [guideId, fetchReport]);

  const exportExcel = async () => {
    if (!guideId) {
      setError('Please select a guide before exporting.');
      return;
    }
    setExporting(true);
    setError('');
    try {
      const qs = buildQuery();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/guide-performance/export-excel?${qs}`,
        { headers: { token } }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(errBody.message || `Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      let filename = 'guide-performance-report.xlsx';
      if (dispo && dispo.includes('filename=')) {
        const m = dispo.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const gridShellSx = {
    '& .MuiDataGrid-root': { border: 'none' },
    '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}` },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: colors.blueAccent[700],
      borderBottom: 'none',
      color: colors.grey[100],
    },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
    '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
    '& .MuiDataGrid-row': {
      color: colors.grey[100],
      '&:hover': { backgroundColor: `${colors.blueAccent[800]}55` },
    },
    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${colors.grey[700]}`,
      backgroundColor: colors.blueAccent[700],
      color: colors.grey[100],
    },
    '& .MuiTablePagination-root': { color: colors.grey[100] },
    '& .MuiIconButton-root': { color: colors.grey[200] },
    '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
      color: `${colors.grey[100]} !important`,
    },
  };

  const columns = [
    { field: 'serial', headerName: '#', width: 64, align: 'center', headerAlign: 'center' },
    {
      field: 'guideFullName',
      headerName: 'Guide',
      minWidth: 140,
      flex: 0.6,
    },
    { field: 'destinationName', headerName: 'Destination', width: 170 },
    { field: 'departureDate', headerName: 'Departure date', width: 120 },
    { field: 'passengerCount', headerName: 'Passengers', width: 108, type: 'number', headerAlign: 'right', align: 'right' },
    { field: 'tripDays', headerName: 'Days', width: 76, type: 'number', headerAlign: 'center', align: 'center' },
    { field: 'busNumbers', headerName: 'Bus', width: 110 },
    {
      field: 'guideAllowanceTotal',
      headerName: 'Guide allowance €',
      width: 140,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => `€ ${Number(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'guideFee',
      headerName: 'Guide fee €',
      width: 130,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => `€ ${Number(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'ttlCollected',
      headerName: 'TTL Collected €',
      width: 130,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => `€ ${Number(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'discount',
      headerName: 'Discount €',
      width: 118,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => `€ ${Number(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'mileage',
      headerName: 'Mileage',
      width: 110,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'averageSalesPerPassenger',
      headerName: 'Avg / passenger €',
      width: 140,
      type: 'number',
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => `€ ${Number(params.value || 0).toFixed(2)}`,
    },
    {
      field: 'surveyCount',
      headerName: 'Surveys',
      width: 80,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => params.value ?? '—',
    },
    {
      field: 'q1Rating',
      headerName: 'Guide Performance',
      width: 148,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => (params.value != null ? `${Number(params.value).toFixed(2)} ★` : '—'),
    },
    {
      field: 'q2Rating',
      headerName: 'Presentation & Knowledge',
      width: 190,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => (params.value != null ? `${Number(params.value).toFixed(2)} ★` : '—'),
    },
    {
      field: 'q3Rating',
      headerName: 'Organization & Punctuality',
      width: 196,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => (params.value != null ? `${Number(params.value).toFixed(2)} ★` : '—'),
    },
    {
      field: 'q4Rating',
      headerName: 'Friendliness',
      width: 120,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (params) => (params.value != null ? `${Number(params.value).toFixed(2)} ★` : '—'),
    },
  ];

  return (
    <Box m="20px">
      <Header title="Guide performance" />

      <Card
        sx={{
          mb: 3,
          background: colors.primary[400],
          border: `1px solid ${colors.grey[700]}`,
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" fontWeight="600" sx={{ color: colors.grey[100], mb: 1 }}>
            Report filters
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[400], mb: 2 }}>
            Choose a guide — the report loads automatically for <strong>all trips</strong> (every destination and
            departure date that has bookings for that guide).
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={7} md={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="gpr-guide-label" shrink sx={labelSx(colors)}>
                  Guide
                </InputLabel>
                <Select
                  labelId="gpr-guide-label"
                  label="Guide"
                  displayEmpty
                  value={guideId}
                  onChange={(e) => setGuideId(e.target.value)}
                  sx={inputSx(colors)}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  renderValue={(v) =>
                    v === '' ? (
                      <Typography component="span" sx={{ color: colors.grey[400], fontStyle: 'italic' }}>
                        Choose a guide…
                      </Typography>
                    ) : (
                      selectedGuideLabel || v
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Choose a guide…</em>
                  </MenuItem>
                  {guides.map((g) => (
                    <MenuItem key={g.id} value={String(g.id)}>
                      {[g.name, g.surname].filter(Boolean).join(' ')} — {g.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5} md="auto">
              {can('reports.guide_performance.export') && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlinedIcon />}
                  onClick={exportExcel}
                  disabled={exporting || !guideId}
                  sx={{
                    px: 3,
                    py: 1,
                    color: colors.grey[100],
                    borderColor: colors.grey[500],
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: colors.greenAccent[400], backgroundColor: `${colors.greenAccent[900]}22` },
                  }}
                >
                  Download Excel
                </Button>
              )}
            </Grid>
            {selectedGuideLabel ? (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  Guide: <strong style={{ color: colors.greenAccent[400] }}>{selectedGuideLabel}</strong>
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {totals ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={EventNoteIcon}
              label="Trips in view"
              value={totals.tripCount}
              sub="All destinations & departure dates"
              colors={colors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={EuroSymbolIcon}
              label="Total collected"
              value={`€ ${Number(totals.totalTtlCollected || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
              sub="Excursions + packages"
              colors={colors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SpeedOutlinedIcon}
              label="Mileage"
              value={Number(totals.totalMileage || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              colors={colors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={LocalOfferOutlinedIcon}
              label="Discount"
              value={`€ ${Number(totals.totalDiscount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
              sub="Manual discount applied"
              colors={colors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={WalletOutlinedIcon}
              label="Guide allowance total"
              value={`€ ${Number(totals.totalGuideAllowance || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
              sub="Manual per-trip total from guide app"
              colors={colors}
            />
          </Grid>
          {totals.avgQ1 != null ? (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={StarIcon} label="Guide Performance" value={`${Number(totals.avgQ1).toFixed(2)} / 5`} colors={colors} />
            </Grid>
          ) : null}
          {totals.avgQ2 != null ? (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={StarIcon} label="Presentation & Knowledge" value={`${Number(totals.avgQ2).toFixed(2)} / 5`} colors={colors} />
            </Grid>
          ) : null}
          {totals.avgQ3 != null ? (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={StarIcon} label="Organization & Punctuality" value={`${Number(totals.avgQ3).toFixed(2)} / 5`} colors={colors} />
            </Grid>
          ) : null}
          {totals.avgQ4 != null ? (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard icon={StarIcon} label="Friendliness" value={`${Number(totals.avgQ4).toFixed(2)} / 5`} colors={colors} />
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${colors.grey[700]}`,
          overflow: 'hidden',
          backgroundColor: colors.primary[400],
        }}
      >
        <Box px={2} py={1.5} borderBottom={`1px solid ${colors.grey[700]}`}>
          <Typography variant="subtitle1" fontWeight="600" sx={{ color: colors.grey[100] }}>
            Trip breakdown
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            Use the toolbar to search or filter columns. One row per destination and departure date.{' '}
            <strong>Reserved</strong> is add-on excursion quantity booked but not yet paid.
          </Typography>
        </Box>
        <Box height="min(640px, calc(100vh - 420px))" minHeight={360} sx={gridShellSx}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            components={{ Toolbar: ReportToolbar }}
            getRowId={(r) => `${r.countryId}-${r.departureDate}-${r.guideId}`}
            pagination
            pageSize={pageSize}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowsPerPageOptions={[10, 25, 50, 100]}
            localeText={{ noRowsLabel: 'No bookings found for this guide. Choose another guide or check data.' }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnSeparator': { display: 'none' },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default GuidePerformanceReport;
